import PropTypes from 'prop-types';
import styled from 'styled-components';
import React from 'react';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';
import media from '../../styles/media.style';
import Placeholder from '../../components/Placeholder';
import Markdown from '../../components/Markdown';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';
import { TABLET_P } from '../../constants';

const Title = styled.div`
  text-align: center;
  font-size: 38px;
  letter-spacing: 4px;
  margin-bottom: 99px;
  padding: 0 15px; 

  @media (min-width: 600px) {
    font-size: 38px;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 99px;
  }
`;

const CreatorsCover = styled.section`
  margin: 89px auto;
  max-width: 1060px;
  width: 100%;
  padding:0 20px;
  h4 {
    display:flex;
    margin: 15px auto 10px;
    padding: 0 30px;
    height: 28px;
    font-family: "Hiragino UD Sans Std";
    font-size: 14px;
    color: #000;
    justify-content: left;
    align-items: center;
  }
  ${media.tabletL`
    padding: 0 0;
  `};
`;

const CreatorList = styled.ul`
  margin:0;
  padding: 0;
  list-style-type: none;
  ${media.tabletL`
  `};
`;
const CreatorListItem = styled.li`
  margin-bottom: 80px;
  ${media.tabletL`
    display: flex;
    margin-bottom: 120px;
  `};
`;
const CreatorImageColumn = styled.div`
  img {
    width: 100%;
    margin-bottom: 20px;
  }
  ${media.tabletL`
    width: 50%;
    padding-right: 60px;
    img {
      margin-bottom: 0;
    }
  `};
`;
const CreatorImage = styled.img``;
const CreatorProfileColumn = styled.div`
  
  .title { font-size:14px; margin-bottom: 10px; }
  .name-en { font-size:14px; font-weight: bold; }
  .name { font-size: 30px; margin-bottom: 5px; }
  .description { font-size: 14px; margin-bottom: 10px; }
  .works-link { display: inline-block; font-size: 14px; margin-bottom: 15px; text-decoration: underline; }
  .seminar-label { font-size: 14px; }

  ${media.tabletL`
    flex: 1;
    padding-left: 60px;
  `};
`;

const CreatorDescription =  styled.div`
  font-size: 14px;
  margin-bottom: 10px;
  p, li {
    font-size: inherit;
  }
`;

const SeminarList = styled.ul`
  list-style-type:none;
  a {
    text-decoration: underline;
  }
`;

/**
 * 「セミナー」画面 > 「セミナーのご案内」画面 > インストラクター > 「CREATORS」画面
 */
const CreatorsPage = ({ data, locale }) => (
  <React.Fragment>
    {_.get(data, 'metaData') && (
      <Helmet
        title={_.get(data.metaData, 'title')}
        link={[makeCanonicalTagObj(locale, '/creators'), ...makeHreflangTagArr(data.metaData.region, '/creators')]}
        meta={[
          {
            name: 'description',
            content: _.get(data.metaData, 'description'),
          },
          {
            name: 'keyword',
            content: _.get(data.metaData, 'keyword'),
          },
          // TODO: OGP タグは一旦保留 (2020年01月07日)
          // {
          //   property: 'og:url',
          //   content: _.get(data.metaData, 'url'),
          // },
          // {
          //   property: 'og:type',
          //   content: _.get(data.metaData, 'type'),
          // },
          // {
          //   property: 'og:title',
          //   content: _.get(data.metaData, 'title'),
          // },
          // {
          //   property: 'og:image',
          //   content: _.get(data.metaData, 'image.file.url'),
          // },
          // {
          //   property: 'og:description',
          //   content: _.get(data.metaData, 'description'),
          // },
          // {
          //   property: 'og:site_name',
          //   content: _.get(data.metaData, 'siteName'),
          // },
        ]}
      />
    )}
    <CreatorsCover>
      <Title>{data.copy.creators_title || <Placeholder h="30px" w="200px" />}</Title>
      <CreatorList>
        {data.creators.map(creator => (
          <CreatorListItem key={creator.entryId}>
            <CreatorImageColumn>
              <CreatorImage src={creator.image.file.url} alt={creator.image.file.title} />
            </CreatorImageColumn>
            <CreatorProfileColumn>
              <div className="title">{creator.title}</div>
              <div className="name-en">{creator.nameEn}</div>
              <div className="name">{creator.name}</div>
              {creator.description && creator.description !== '' ? (
                <Markdown src={creator.description} Component={CreatorDescription} />
              ) : null}
              {(() => {
                if (creator.linkUrl !== undefined) {
                  return <a href={creator.linkUrl} className="works-link" rel="noreferrer" target="_blank">{data.copy.more_infomation}</a>
                }
              })()}
              <div className="seminar-label">[ {data.copy.work_seminar} ]</div>
              <SeminarList className="seminars">
                {creator.seminar.map((seminar, i) => (
                  <li key={creator.entryId+seminar.entryId+i}>
                    <a href={`/${locale}/education/${seminar.slug}`}>{seminar.name}</a>
                  </li>
                ))}
              </SeminarList>
            </CreatorProfileColumn>
          </CreatorListItem>
        ))}
      </CreatorList>
    </CreatorsCover>
    {!(locale && data && _.get(data, 'copy.creators_title') && _.get(data, 'breadcrumbs')) ? (
      <Placeholder w="200px" h="24px" m={window.innerWidth > TABLET_P ? '0 0 20px 40px' : '0 0 20px 20px'} />
    ) : (
      <BreadCrumbMenu
        currentPath={`https://www.shiseido-professional.com/${locale}/creators`}
        items={[
          {
            name: 'TOP',
            path: `/${locale}/`,
          },
          {
            name: data.breadcrumbs.Breadcrumbs1.name,
            path: `/${locale}/education`,
          },
          {
            name: data.breadcrumbs.Breadcrumbs2.name,
            path: `/${locale}/seminar-schedule`,
          },
          {
            name: data.copy.creators_title.toUpperCase(),
            path: null,
          },
        ]}
      />
    )}
  </React.Fragment>
);

CreatorsPage.defaultProps = {
  data: {
    creators: [],
    copy:{
      creators_title: undefined,
      more_infomation: undefined,
      work_seminar: undefined,
    },
  },
};

CreatorsPage.propTypes = {
  data: PropTypes.shape(),
};

export default CreatorsPage;



// WEBPACK FOOTER //
// ./src/pages/CreatorsPage/CreatorsPage.js